import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import { useLocale } from "next-intl";

import { ISeoMetadata } from "@/types/seo";
import { createLogger } from "@/utils/logger";

const logger = createLogger("hook-use-seo-meta");

/**
 * Хук для получения SEO метаданных для текущей страницы
 *
 * @param url - URL-путь страницы (без домена); если не указан, берется из текущего пути
 * @returns Объект с SEO метаданными
 */
export function useSeoMeta(url?: string): {
  isLoading: boolean;
  error: Error | null;
  data: ISeoMetadata | null;
} {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = useLocale();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<ISeoMetadata | null>(null);
  const [_lastFetched, setLastFetched] = useState<number>(0);

  // Формируем полный путь с учетом языкового префикса и параметров запроса
  const buildFullUrl = (): string => {
    if (url) {
      return url;
    }

    // Проверяем, начинается ли путь с локали
    const hasLocalePrefix = pathname.startsWith(`/${locale}/`);
    let fullPath = pathname;

    // Ensure the path has locale prefix for the API call
    if (!hasLocalePrefix && !pathname.startsWith("/api/")) {
      fullPath = `/${locale}${pathname === "/" ? "" : pathname}`;
    }

    // Добавляем query-параметры если они есть
    const queryString = searchParams.toString();
    return `${fullPath}${queryString ? `?${queryString}` : ""}`;
  };

  const pageUrl = buildFullUrl();

  useEffect(() => {
    const fetchSeoData = async () => {
      if (!pathname) {
        return;
      }
      setIsLoading(true);
      setError(null);
      logger.info(
        `Fetching SEO meta for pathname: ${pathname}, locale: ${locale}`,
      );
      try {
        const apiUrl = `${process.env.NEXT_PUBLIC_BASE_URL || ""}/seo/meta`;
        const params = new URLSearchParams({
          url: pageUrl,
          locale: locale,
        });

        const response = await fetch(`${apiUrl}?${params.toString()}`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          // Удалено next: { revalidate } - эта опция работает только на сервере
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch SEO metadata: ${response.statusText}`,
          );
        }

        const result = await response.json();

        if (result.success && result.data) {
          setData(result.data);
          logger.info("Fetched SEO meta data:", result.data);
        } else if (result.data) {
          // Для случая, когда API не обертывает в success / data
          setData(result);
          logger.info("Fetched SEO meta data:", result);
        } else {
          throw new Error("Invalid response format");
        }

        // Обновляем время последнего успешного запроса
        setLastFetched(Date.now());
      } catch (err: any) {
        logger.error("Error fetching SEO meta data:", err);
        setError(err.message || "Failed to fetch SEO data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSeoData();
  }, [pathname, locale, pageUrl]);

  return { isLoading, error, data };
}
